import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { SidebarProvider } from '@shared/ui/sidebar';
import { Topbar } from './topbar';
import { AppSidebar } from './leftbar';
import { ContentFrame } from './ContentFrame';
import { RecentSidebar, useTrackVisits } from './rightbar';
import { PageSpinner, Toaster } from '@shared/patterns';
import { useAuth } from '@shared/auth/AuthContext';
import { useLayoutPrefs } from './layoutPrefs';
import { useShellShortcuts } from './useShellShortcuts';
import { useIsMobile } from '@shared/hooks/use-breakpoint';
import { MobileBottomNav } from './mobile/MobileBottomNav';
import { MobileLauncherSheet } from './mobile/MobileLauncherSheet';
import { MobileSearchDialog } from './mobile/MobileSearchDialog';

export const ShellLayout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { leftCollapsed, setLeftCollapsed } = useLayoutPrefs();
  const isMobile = useIsMobile();

  useTrackVisits();
  useShellShortcuts();

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-background text-foreground">
        <PageSpinner />
        <p className="text-sm text-muted-foreground mt-4">Connecting to Helios Cloud...</p>
      </div>
    );
  }

  if (!isAuthenticated && location.pathname !== '/login') {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  const isStandaloneStudio =
    location.pathname === '/webbuilder/pages' ||
    location.pathname.startsWith('/webbuilder/pages/');

  if (isStandaloneStudio) {
    return (
      <div className="h-screen supports-[height:100dvh]:h-dvh w-screen overflow-hidden bg-background text-foreground flex flex-col">
        <React.Suspense fallback={<PageSpinner />}>
          <div className="flex-1 w-full h-full overflow-hidden">
            <Outlet />
          </div>
        </React.Suspense>
        {isMobile && (
          <>
            <MobileLauncherSheet />
            <MobileSearchDialog />
          </>
        )}
        <Toaster
          position={isMobile ? 'top-center' : 'bottom-right'}
          offset={isMobile ? '8px' : undefined}
        />
      </div>
    );
  }

  return (
    <SidebarProvider
      open={!leftCollapsed}
      onOpenChange={(open) => setLeftCollapsed(!open)}
      className="flex flex-col h-screen supports-[height:100dvh]:h-dvh w-full overflow-hidden bg-background"
    >
      <Topbar />
      <div className="flex-1 flex overflow-hidden">
        <AppSidebar />
        <ContentFrame />
        <RecentSidebar />
      </div>
      <MobileBottomNav />
      <MobileLauncherSheet />
      <MobileSearchDialog />
      <Toaster
        position={isMobile ? 'top-center' : 'bottom-right'}
        offset={isMobile ? 'calc(var(--shell-header-h) + 8px)' : undefined}
      />
    </SidebarProvider>
  );
};
